import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap } from 'rxjs';

import { getApiErrorMessage } from '../../../core/utils/api-error.util';
import { DashboardApiService } from '../services/dashboard-api.service';
import { DashboardActions } from './dashboard.actions';

@Injectable()
export class DashboardEffects {
  private readonly actions$ = inject(Actions);
  private readonly dashboardApi = inject(DashboardApiService);

  readonly operationalOverview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.loadOverview, DashboardActions.refreshOperationalOverview),
      switchMap((action) =>
        this.dashboardApi.getOverview(action.filters, action.access, action.requests).pipe(
          map((overview) =>
            action.type === DashboardActions.refreshOperationalOverview.type
              ? DashboardActions.refreshOperationalOverviewSuccess({ overview })
              : DashboardActions.loadOverviewSuccess({ overview }),
          ),
          catchError((error: unknown) =>
            of(
              action.type === DashboardActions.refreshOperationalOverview.type
                ? DashboardActions.refreshOperationalOverviewFailure({
                    error: getApiErrorMessage(error, 'Unable to refresh dashboard overview'),
                  })
                : DashboardActions.loadOverviewFailure({
                    error: getApiErrorMessage(error, 'Unable to load dashboard overview'),
                  }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly loadPartnerTracking$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.loadPartnerTracking),
      switchMap(({ payload }) =>
        this.dashboardApi.getPartnerTracking(payload).pipe(
          map((tracking) => DashboardActions.loadPartnerTrackingSuccess({ tracking })),
          catchError((error: unknown) =>
            of(
              DashboardActions.loadPartnerTrackingFailure({
                error: getApiErrorMessage(error, 'Unable to load live partner locations'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly refreshPartnersOverview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.refreshPartnersOverview),
      switchMap(({ filters, statuses }) =>
        this.dashboardApi.getPartnersOverview(filters, statuses).pipe(
          map(({ partners }) => DashboardActions.refreshPartnersOverviewSuccess({ partners })),
          catchError((error: unknown) =>
            of(
              DashboardActions.refreshPartnersOverviewFailure({
                error: getApiErrorMessage(error, 'Unable to refresh partner overview'),
              }),
            ),
          ),
        ),
      ),
    ),
  );
}
